import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import NotificationsBell from './NotificationsBell';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { tc } from '../../i18n/common';

const AppLayout = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSuperUser, setIsSuperUser] = useState(false); // Add superuser state
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    
    // Check for URL language parameter on location changes

    const [locale, setLocale] = useState(() => {
        // Check for URL parameter first, then localStorage, then default to 'en'
        const urlParams = new URLSearchParams(window.location.search);
        const urlLang = urlParams.get('l');
        const supportedLanguages = ['de', 'en', 'fr', 'it', 'es'];

        if (urlLang && supportedLanguages.includes(urlLang)) {
            // Save URL language to localStorage for persistence
            localStorage.setItem('locale', urlLang);
            return urlLang;
        }

        return localStorage.getItem('locale') || 'en';
    });

    useEffect(() => {
        const loadUser = async () => {
            try {
                // Use baseURL-relative path. bootstrap.js sets axios.defaults.baseURL to '/api'
                const res = await axios.post('/user');
                setUser(res.data || null);

                // Check permissions
                // Check permissions
                if (import.meta.env.DEV) {

                    setIsSuperUser(true);
                } else {
                    const token = localStorage.getItem('token');
                    if (token) {
                        try {
                            const azureToken = localStorage.getItem('azure_token');
                            const permRes = await axios.get('/azure/permissions', {
                                 headers: { 
                                     Authorization: `Bearer ${token}`,
                                     'X-Azure-Token': azureToken
                                 }
                            });
                            if (permRes.data && permRes.data.modulePermissions && permRes.data.modulePermissions.superuser === true) {
                                setIsSuperUser(true);
                            } else {
                                setIsSuperUser(false);
                            }
                        } catch (permError) {
                           console.error('Failed to check permissions in layout', permError);
                           setIsSuperUser(false);
                        }
                    }
                }

            } catch (e) {
                // If unauthorized, send to login
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };
        loadUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check for URL language parameter on location changes
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const urlLang = urlParams.get('l');
        const supportedLanguages = ['de', 'en', 'fr', 'it', 'es'];

        if (urlLang && supportedLanguages.includes(urlLang) && urlLang !== locale) {
            setLocale(urlLang);
            localStorage.setItem('locale', urlLang);
            // Dispatch event to notify other components
            window.dispatchEvent(new Event('locale-changed'));
        }
    }, [location.search, locale]);

    // Listen for locale changes
    useEffect(() => {
        const handleLocaleChange = () => {
            const newLocale = localStorage.getItem('locale') || 'en';
            setLocale(newLocale);
        };
        window.addEventListener('locale-changed', handleLocaleChange);
        return () => window.removeEventListener('locale-changed', handleLocaleChange);
    }, []);

    const isAdmin = () => {
        const rr = (user?.azure_roles || []).map(r => String(r).toLowerCase());
        const orr = (user?.azure_organization_roles || []).map(r => String(r).toLowerCase());
        return rr.includes('admin') || orr.includes('admin');
    };

    if (loading) return null;

    const headerHeight = 56;

    const inIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <header style={{
                display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', gap: 12,
                padding: '10px 16px', borderBottom: '1px solid #eee', position: 'sticky', top: 0, background: '#fff', zIndex: 1, color: '#222'
            }}>
                {/* Responsive header styles */}
                <style>{`
                    @media (min-width: 768px) {
                        .desktop-nav { display: flex !important; }
                        .mobile-toggle { display: none !important; }
                    }
                    @media (max-width: 767px) {
                        .desktop-nav { display: none !important; }
                        .mobile-toggle { display: inline-flex !important; }
                    }
                    .nav-link { padding: 6px 8px; border-radius: 8px; color: #222; text-decoration: none; }
                    .nav-link.active { color: var(--color-primary); background: transparent; }
                `}</style>
                <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: '#222' }}>
                    <img src="/images/logo/leadersoft-client-plattform.svg" alt="Client Plattform" style={{ height: 24 }} />
                </Link>
                <nav className="desktop-nav" style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Link to="/releases" className={`nav-link ${location.pathname.startsWith('/releases') ? 'active' : ''}`}>{tc('nav.releases', locale)}</Link>
                    <Link to="/videos" className={`nav-link ${location.pathname.startsWith('/videos') ? 'active' : ''}`}>{tc('nav.videos', locale)}</Link>
                    {user?.is_azure_user && user?.azure_tenant_id && isSuperUser && (
                        <>
                            <Link to="/organigram" className={`nav-link ${location.pathname.startsWith('/organigram') ? 'active' : ''}`}>{tc('nav.organigram', locale)}</Link>
                            <Link to="/admin/coach-insight" className={`nav-link ${location.pathname.startsWith('/admin/coach-insight') ? 'active' : ''}`}>{tc('coachInsight.defaultTitle', locale)}</Link>
                        </>
                    )}
                    {isAdmin() && (
                        <>
                            <Link to="/admin/releases" className={`nav-link ${location.pathname.startsWith('/admin/releases') ? 'active' : ''}`}>{tc('nav.adminReleases', locale)}</Link>
                            <Link to="/admin/videos" className={`nav-link ${location.pathname.startsWith('/admin/videos') ? 'active' : ''}`}>{tc('nav.adminVideos', locale)}</Link>
                            <Link to="/admin/tasks" className={`nav-link ${location.pathname.startsWith('/admin/tasks') ? 'active' : ''}`}>{tc('nav.adminTasks', locale)}</Link>
                            <Link to="/admin/tasks" className={`nav-link ${location.pathname.startsWith('/admin/tasks') ? 'active' : ''}`}>{tc('nav.adminTasks', locale)}</Link>
                            <Link to="/admin/settings" className={`nav-link ${location.pathname.startsWith('/admin/settings') ? 'active' : ''}`}>{tc('nav.settings', locale)}</Link>
                        </>
                    )}
                </nav>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifySelf: 'end' }}>
                    {!inIframe && (
                    <Select
                        size="small"
                        value={locale}
                        optionLabelProp="label"
                        onChange={(v) => { setLocale(v); localStorage.setItem('locale', v); window.dispatchEvent(new Event('locale-changed')); }}
                        options={[
                            { value: 'de', label: (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <img src="/images/flags/ch.svg" alt="Deutsch" style={{ width: 16, height: 12, objectFit: 'cover', borderRadius: 2 }} />
                                    <span>Deutsch</span>
                                </span>
                            ) },
                            { value: 'en', label: (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <img src="/images/flags/us.svg" alt="English" style={{ width: 16, height: 12, objectFit: 'cover', borderRadius: 2 }} />
                                    <span>English</span>
                                </span>
                            ) },
                            { value: 'fr', label: (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <img src="/images/flags/fr.svg" alt="Français" style={{ width: 16, height: 12, objectFit: 'cover', borderRadius: 2 }} />
                                    <span>Français</span>
                                </span>
                            ) },
                            { value: 'it', label: (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <img src="/images/flags/it.svg" alt="Italiano" style={{ width: 16, height: 12, objectFit: 'cover', borderRadius: 2 }} />
                                    <span>Italiano</span>
                                </span>
                            ) },
                        ]}
                        style={{ width: 150 }}
                    />
                    )}
                    <NotificationsBell />
                    <button aria-label="Toggle menu" onClick={() => setMobileOpen(!mobileOpen)} className="mobile-toggle" style={{ background: 'transparent', border: 0, color: '#222', fontSize: 18, display: 'none', alignItems: 'center' }}>
                        {mobileOpen ? <CloseOutlined /> : <MenuOutlined />}
                    </button>
                </div>
            </header>
            {/* Mobile menu */}
            {mobileOpen && (
                <div style={{ background: '#fff', color: '#222', borderBottom: '1px solid #eee', padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Link to="/releases" onClick={() => setMobileOpen(false)} style={{ padding: '8px 4px', color: location.pathname.startsWith('/releases') ? 'var(--color-primary)' : '#222' }}>{tc('nav.releases', locale)}</Link>
                    <Link to="/videos" onClick={() => setMobileOpen(false)} style={{ padding: '8px 4px', color: location.pathname.startsWith('/videos') ? 'var(--color-primary)' : '#222' }}>{tc('nav.videos', locale)}</Link>
                    {user?.is_azure_user && user?.azure_tenant_id && isSuperUser && (
                        <>
                            <Link to="/organigram" onClick={() => setMobileOpen(false)} style={{ padding: '8px 4px', color: location.pathname.startsWith('/organigram') ? 'var(--color-primary)' : '#222' }}>{tc('nav.organigram', locale)}</Link>
                            <Link to="/admin/coach-insight" onClick={() => setMobileOpen(false)} style={{ padding: '8px 4px', color: location.pathname.startsWith('/admin/coach-insight') ? 'var(--color-primary)' : '#222' }}>{tc('coachInsight.defaultTitle', locale)}</Link>
                        </>
                    )}
                    {isAdmin() && (
                        <>
                            <Link to="/admin/releases" onClick={() => setMobileOpen(false)} style={{ padding: '8px 4px', color: location.pathname.startsWith('/admin/releases') ? 'var(--color-primary)' : '#222' }}>{tc('nav.adminReleases', locale)}</Link>
                            <Link to="/admin/videos" onClick={() => setMobileOpen(false)} style={{ padding: '8px 4px', color: location.pathname.startsWith('/admin/videos') ? 'var(--color-primary)' : '#222' }}>{tc('nav.adminVideos', locale)}</Link>
                            <Link to="/admin/tasks" onClick={() => setMobileOpen(false)} style={{ padding: '8px 4px', color: location.pathname.startsWith('/admin/tasks') ? 'var(--color-primary)' : '#222' }}>{tc('nav.adminTasks', locale)}</Link>
                            <Link to="/admin/tasks" onClick={() => setMobileOpen(false)} style={{ padding: '8px 4px', color: location.pathname.startsWith('/admin/tasks') ? 'var(--color-primary)' : '#222' }}>{tc('nav.adminTasks', locale)}</Link>
                            <Link to="/admin/settings" onClick={() => setMobileOpen(false)} style={{ padding: '8px 4px', color: location.pathname.startsWith('/admin/settings') ? 'var(--color-primary)' : '#222' }}>{tc('nav.settings', locale)}</Link>
                        </>
                    )}
                </div>
            )}
            <main style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {children}
            </main>


        </div>
    );
};

export default AppLayout;
